import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PilotageDashboard from './pages/PilotageDashboard';
import EmployeDetail from './pages/EmployeDetail';
import FicheEmploye from './pages/FicheEmploye';
import Administration from './pages/Administration';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Logs from './pages/Logs';
import Permissions from './pages/Permissions';
import Departements from './pages/Departements';
import Titres from './pages/Titres';
import TypesContrat from './pages/TypesContrat';
import TypesPersonnel from './pages/TypesPersonnel';
import TypesConge from './pages/TypesConge';
import Filiales from './pages/Filiales';
import Annees from './pages/Annees';
import Banques from './pages/Banques';
import Sanctions from './pages/Sanctions';
import PersonnelSanctions from './pages/PersonnelSanctions';
import Certificats from './pages/Certificats';
import Mailing from './pages/Mailing';
import MisesAJour from './pages/MisesAJour';
import Profils from './pages/Profils';
import Clients from './pages/Clients';
import Activites from './pages/Activites';
import Campagnes from './pages/Campagnes';
import Marques from './pages/Marques';
import EspaceEmploye from './pages/EspaceEmploye';
import Indicateurs from './pages/Indicateurs';
import IndicateurAssociation from './pages/IndicateurAssociation';
import ImportPerformance from './pages/ImportPerformance';
import ImportIndicateurs from './pages/ImportIndicateurs';
import PerformanceTypes from './pages/PerformanceTypes';
import PerformancePersonnel from './pages/PerformancePersonnel';
import ActivitesModePaiement from './pages/Activites';
import TypesPersonnelConfig from './pages/TypesPersonnelConfig';
import NotePonderee from './pages/NotePonderee';
import ConditionsPaie from './pages/ConditionsPaie';
import EmployesSortis from './pages/EmployesSortis';
import ConfigPaie from './pages/ConfigPaie';
import FraisMissions from './pages/FraisMissions';
import AvancesSalaire from './pages/AvancesSalaire';
import RetenuesSalaire from './pages/RetenuesSalaire';
import Regularisations from './pages/Regularisations';
import CaisseSociale from './pages/CaisseSociale';
import ExclusionsCharges from './pages/ExclusionsCharges';
import ElementsPaie from './pages/ElementsPaie';
import TypesPersonnelPaie from './pages/TypesPersonnelPaie';
import CalculPaie from './pages/CalculPaie';
import PersonnelNonIntegre from './pages/PersonnelNonIntegre';
import PersonnelStc from './pages/PersonnelStc';
import ValidationConges from './pages/ValidationConges';
import PlanningConges from './pages/PlanningConges';
import Notifications from './pages/Notifications';
import GestionSoldesConge from './pages/GestionSoldesConge';
import Contrats from './pages/Contrats';
import SuiviContrats from './pages/SuiviContrats';
import ReportingRH from './pages/ReportingRH';
import ReportAnnuel from './pages/ReportAnnuel';
import Absences from './pages/Absences';
import GrillesPersonnel from './pages/GrillesPersonnel';
import MouvementsCarriere from './pages/MouvementsCarriere';
import PlansCarriere from './pages/PlansCarriere';
import SuccessionPlanning from './pages/SuccessionPlanning';
import EntretiensCarriere from './pages/EntretiensCarriere';
import DemandesMobilite from './pages/DemandesMobilite';
import ReportingCarriere from './pages/ReportingCarriere';
import NomenclatureMatricule from './pages/NomenclatureMatricule';
import ImportPersonnel from './pages/ImportPersonnel';
import Offres from './pages/Offres';
import Candidatures from './pages/Candidatures';
import CandidatureDetail from './pages/CandidatureDetail';
import Questionnaires from './pages/Questionnaires';
import PortailRecrutement from './pages/PortailRecrutement';
import ReportingRecrutement from './pages/ReportingRecrutement';
import ReportingPaie from './pages/ReportingPaie';
import ReportingPerformance from './pages/ReportingPerformance';
import Communication from './pages/Communication';
import ExportEtats from './pages/ExportEtats';
import ConfigurationExoneration from './pages/ConfigurationExoneration';
import ExportFichePaie from './pages/ExportFichePaie';
import AdminLayout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FilialeProvider } from './context/FilialeContext';

const Personnel = lazy(() => import('./pages/Personnel'));

const ScreenLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-night-blue text-sm text-white">
    Chargement de la console…
  </div>
);

const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <ScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  
  return (
    <FilialeProvider>
      <AdminLayout />
    </FilialeProvider>
  );
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <ScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;

  // Tout le monde authentifié peut accéder au dashboard
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <ScreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/login"
            element={(
              <PublicRoute>
                <Login />
              </PublicRoute>
            )}
          />
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<AdminRoute><Dashboard /></AdminRoute>} />
            <Route path="/pilotage" element={<AdminRoute><PilotageDashboard /></AdminRoute>} />
            <Route path="/espace-employe" element={<EspaceEmploye />} />
            <Route path="/personnel" element={<Suspense fallback={<div>Chargement...</div>}><Personnel /></Suspense>} />
            <Route path="/personnel/nouveau" element={<FicheEmploye />} />
            <Route path="/personnel/modifier/:id" element={<FicheEmploye />} />
            <Route path="/employe/:id" element={<EmployeDetail />} />
            <Route path="/administration" element={<Administration />} />
            <Route path="/departements" element={<Departements />} />
            <Route path="/titres" element={<Titres />} />
            <Route path="/types-contrat" element={<TypesContrat />} />
            <Route path="/types-personnel" element={<TypesPersonnel />} />
            <Route path="/types-conge" element={<TypesConge />} />
            <Route path="/gestion-soldes-conge" element={<GestionSoldesConge />} />
            <Route path="/users" element={<Users />} />
            <Route path="/permissions" element={<Permissions />} />
            <Route path="/filiales" element={<Filiales />} />
            <Route path="/annees" element={<Annees />} />
            <Route path="/banques" element={<Banques />} />
            <Route path="/sanctions" element={<Sanctions />} />
            <Route path="/personnel-sanctions" element={<PersonnelSanctions />} />
            <Route path="/absences" element={<Absences />} />
            <Route path="/certificats" element={<Certificats />} />
            <Route path="/mailing" element={<Mailing />} />
            <Route path="/mises-a-jour" element={<MisesAJour />} />
            <Route path="/profils" element={<Profils />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/activites" element={<Activites />} />
            <Route path="/campagnes" element={<Campagnes />} />
            <Route path="/marques" element={<Marques />} />
            <Route path="/espace-employe" element={<EspaceEmploye />} />
            <Route path="/validation-conges" element={<ValidationConges />} />
            <Route path="/planning-conges" element={<PlanningConges />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/contrats/:personnelId" element={<Contrats />} />
            <Route path="/suivi-contrats" element={<SuiviContrats />} />
            <Route path="/reporting-rh" element={<ReportingRH />} />
            <Route path="/report-annuel" element={<ReportAnnuel />} />
            <Route path="/indicateurs" element={<Indicateurs />} />
            <Route path="/indicateurs/type/:typeId" element={<IndicateurAssociation />} />
            <Route path="/import-performance/:typeId" element={<ImportPerformance />} />
            <Route path="/import-indicateurs" element={<ImportIndicateurs />} />
            <Route path="/performance" element={<PerformanceTypes />} />
            <Route path="/performance/:typeId" element={<PerformancePersonnel />} />
            <Route path="/activites" element={<ActivitesModePaiement />} />
            <Route path="/types-personnel/:activiteId" element={<TypesPersonnelConfig />} />
            <Route path="/note-ponderee/:typeId" element={<NotePonderee />} />
            <Route path="/conditions-paie/:typeId" element={<ConditionsPaie />} />
            <Route path="/employes-sortis" element={<EmployesSortis />} />
            <Route path="/frais-missions" element={<FraisMissions />} />
            <Route path="/avance-salaire" element={<AvancesSalaire />} />
            <Route path="/retenue-salaire" element={<RetenuesSalaire />} />
            <Route path="/regularisation" element={<Regularisations />} />
            <Route path="/caisse-sociale" element={<CaisseSociale />} />
            <Route path="/exclusions-charges" element={<ExclusionsCharges />} />
            <Route path="/elements-paie" element={<ElementsPaie />} />
            <Route path="/types-personnel-paie/:activiteId" element={<TypesPersonnelPaie />} />
            <Route path="/calcul-paie/:typeId" element={<CalculPaie />} />
            <Route path="/personnel-non-integre/:typeId" element={<PersonnelNonIntegre />} />
            <Route path="/personnel-stc" element={<PersonnelStc />} />
            <Route path="/periodes-paie" element={<ConfigPaie />} />
            <Route path="/grilles-personnel" element={<GrillesPersonnel />} />
            <Route path="/mouvements-carriere" element={<MouvementsCarriere />} />
            <Route path="/plans-carriere" element={<PlansCarriere />} />
            <Route path="/succession-planning" element={<SuccessionPlanning />} />
            <Route path="/entretiens-carriere" element={<EntretiensCarriere />} />
            <Route path="/demandes-mobilite" element={<DemandesMobilite />} />
            <Route path="/reporting-carriere" element={<ReportingCarriere />} />
            <Route path="/nomenclature-matricule" element={<NomenclatureMatricule />} />
            <Route path="/import-personnel" element={<ImportPersonnel />} />
            <Route path="/offres" element={<Offres />} />
            <Route path="/candidatures" element={<Candidatures />} />
            <Route path="/candidatures/:id" element={<CandidatureDetail />} />
            <Route path="/questionnaires" element={<Questionnaires />} />
            <Route path="/portail" element={<PortailRecrutement />} />
            <Route path="/reporting-recrutement" element={<ReportingRecrutement />} />
            <Route path="/reporting-paie" element={<ReportingPaie />} />
            <Route path="/reporting-performance" element={<ReportingPerformance />} />
            <Route path="/communication" element={<Communication />} />
            <Route path="/export-etats" element={<ExportEtats />} />
            <Route path="/export-fiche-paie" element={<ExportFichePaie />} />
            <Route path="/configuration-exoneration" element={<ConfigurationExoneration />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
